import { useDispatch, useSelector } from "react-redux";
import { paragraphResponse } from "../../store/promptSlice";
import TextToSpeech from "../../templates/TextToSpeech";
import CountdownTimer from "../../templates/CountdownTimer";
import { Mic } from "lucide-react";
import Wave from "../../templates/Wave";
import { useEffect, useState } from "react";

function ParagraphAssessment({ Exit, timeLeft, setTimeLeft }) {
  const [phase, setPhase] = useState("intro"); // intro → paragraph → questions → finished
  const [questionIndex, setQuestionIndex] = useState(0);
  const [timerStarted, setTimerStarted] = useState(false);
  const dispatch = useDispatch();

  const { paragraphData, loading } = useSelector((state) => state.prompt);
  const paragraph = paragraphData?.paragraph || "";
  const questions = paragraphData?.questions || [];

  // Fetch paragraph when entering paragraph phase
  useEffect(() => {
    if (phase === "paragraph") {
      dispatch(paragraphResponse());
    }
  }, [phase, dispatch]);

  // Start timer only when paragraph is loaded
  useEffect(() => {
    if (!loading && paragraph && !timerStarted) {
      setTimerStarted(true);
    }
  }, [loading, paragraph, timerStarted]);

  // End assessment when time runs out
  useEffect(() => {
    if (timeLeft === 0) setPhase("finished");
  }, [timeLeft]);

  const speakOut =
    "In this assessment, you will listen to a short story. After the paragraph, you will have to answer five questions from the paragraph. Click Next Question when ready.";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-br from-gray-100 to-gray-200">
      {/* Intro */}
      {phase === "intro" && (
        <div className="bg-white rounded-2xl shadow-2xl p-10 max-w-2xl border border-gray-200 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">
            📖 Welcome to  Paragraph Assessment
          </h1>
          <p className="text-gray-700 text-lg mb-6">
            In this assessment, you will listen to a short story. After the
            paragraph, you will have to answer five questions from the
            paragraph. Click Next Question when ready.
          </p>
          <TextToSpeech speech={speakOut} onEnd={() => setPhase("paragraph")} />
        </div>
      )}

      {/* Section Header */}
      {(phase === "paragraph" || phase === "questions") && (
        <div className="w-full max-w-3xl mt-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-700">Section C</h2>
            {timerStarted && (
              <CountdownTimer
                label="Time Remaining"
                timeLeft={timeLeft}
                setTimeLeft={setTimeLeft}
              />
            )}
          </div>
        </div>
      )}

      {/* Paragraph */}
      {phase === "paragraph" && (
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl border border-gray-200">
          <p className="text-gray-700 leading-relaxed mb-6">
            {loading
              ? "⏳ Loading paragraph..."
              : paragraph || "No paragraph returned"}
          </p>

          {!loading && paragraph && (
            <TextToSpeech
              speech={paragraph}
              onEnd={() => {
                setQuestionIndex(0);
                setPhase("questions");
              }}
            />
          )}
        </div>
      )}

      {/* Questions */}
      {phase === "questions" && questions.length > 0 && (
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl border border-gray-200 mt-6">
          {/* Mic + Wave */}
          <div className="flex flex-col items-center space-y-4 bg-gray-50 rounded-xl border-2 border-gray-200 p-6 mb-6">
            <div className="w-20 h-20 flex items-center justify-center bg-green-500 text-white rounded-full shadow-lg text-3xl animate-pulse">
              <Mic size={28} />
            </div>
            <Wave color="green" />
            <p className="text-gray-600 font-medium">Answer the question</p>
          </div>

          <TextToSpeech speech={questions[questionIndex]} />

          <div className="flex justify-end mt-6 w-full">
            <button
              type="button"
              onClick={() => {
                if (questionIndex < questions.length - 1) {
                  setQuestionIndex((prev) => prev + 1);
                } else {
                  setPhase("finished");
                }
              }}
              className="px-6 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-300 transition shadow-md"
            >
              {questionIndex === questions.length - 1
                ? "Finish Test"
                : "Next Question"}
            </button>
          </div>
        </div>
      )}

      {/* Finished Modal */}
      {phase === "finished" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-8 rounded-2xl max-w-md w-full shadow-xl text-center space-y-6">
            <p className="text-lg font-medium text-gray-800">
              🎉 You have successfully completed the assessment.
              <br /> Click <strong>Finish Test</strong> to complete the test.
            </p>
            <button
              onClick={Exit}
              className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
            >
              Finish Test
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParagraphAssessment;
